import { useEffect, useState } from "react";
import { Modal } from "antd";
import { CloseOutlined, DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import { modalStyles } from "../../../utils/modalStyles";

const R = {
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  danger: "#DC2626",
  dangerDeep: "#B91C1C",
  dangerTint: "var(--color-danger-tint)",
};

interface Props {
  open: boolean;
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  icon?: React.ReactNode;
  variant?: "danger" | "primary";
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
}

const DeleteConfirmModal = ({
  open,
  title,
  description,
  confirmLabel,
  cancelLabel,
  icon,
  variant = "danger",
  onConfirm,
  onCancel,
}: Props) => {
  const brand = "#D97706";
  const brandDeep = "#B45309";
  const brandTint = "var(--color-brand-tint)";

  const chipBg = variant === "danger" ? R.dangerTint : brandTint;
  // Текст върху тинт — реактивен на темата (плътните hover фонове остават).
  const chipColor =
    variant === "danger" ? "var(--color-danger-ink)" : "var(--color-brand-deep-ink)";
  const primaryBg = variant === "danger" ? R.danger : brand;
  const primaryHover = variant === "danger" ? R.dangerDeep : brandDeep;
  const primaryDisabled =
    variant === "danger" ? "rgba(220,38,38,0.55)" : "rgba(217,119,6,0.55)";
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setIsSubmitting(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // preventDefault: без него същото натискане минава и през глобалния
        // Esc-„назад" на SharedAccountGate (правило от CLAUDE.md)
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm();
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalWidth = isMobile ? undefined : isTablet ? 420 : 440;

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={modalWidth}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <style>{`
        @keyframes rspin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
      <div style={{ background: R.surface }}>
        {/* Header */}
        <div
          style={{
            padding: isNarrow ? "14px 16px" : "18px 22px",
            borderBottom: `1px solid ${R.border}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: chipBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: chipColor,
              flexShrink: 0,
            }}
          >
            {icon ?? <DeleteOutlined style={{ fontSize: 18 }} />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: isNarrow ? 16 : 17,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: R.ink,
                lineHeight: 1.25,
              }}
            >
              {title}
            </div>
            {description && (
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: R.textSoft,
                  lineHeight: 1.45,
                  marginTop: 6,
                }}
              >
                {description}
              </div>
            )}
          </div>
          <button
            onClick={onCancel}
            aria-label="Close"
            style={{
              height: 30,
              width: 30,
              borderRadius: 6,
              background: "transparent",
              border: "none",
              color: R.textMuted,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "background 120ms, color 120ms",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-shade-1)";
              e.currentTarget.style.color = R.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = R.textMuted;
            }}
          >
            <CloseOutlined style={{ fontSize: 14 }} />
          </button>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: isNarrow ? "12px 16px" : "14px 22px",
            display: "flex",
            flexDirection: "row",
            gap: 10,
            background: R.surface,
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            style={{
              height: 42,
              padding: "0 18px",
              borderRadius: 10,
              border: `1px solid ${R.border}`,
              background: "transparent",
              color: R.textSoft,
              fontSize: 13.5,
              fontWeight: 600,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              transition: "background 120ms",
              flex: 1,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting)
                e.currentTarget.style.background = "var(--color-shade-1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            {cancelLabel ?? t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting}
            style={{
              height: 42,
              padding: "0 22px",
              borderRadius: 10,
              border: "none",
              background: isSubmitting ? primaryDisabled : primaryBg,
              color: "#fff",
              fontSize: 13.5,
              fontWeight: 700,
              letterSpacing: -0.2,
              cursor: isSubmitting ? "not-allowed" : "pointer",
              transition: "background 120ms",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              flex: 1,
            }}
            onMouseEnter={(e) => {
              if (!isSubmitting)
                e.currentTarget.style.background = primaryHover;
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting) e.currentTarget.style.background = primaryBg;
            }}
          >
            {isSubmitting && (
              <span
                style={{
                  width: 14,
                  height: 14,
                  border: "2px solid rgba(255,255,255,0.5)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "rspin 900ms linear infinite",
                }}
              />
            )}
            {confirmLabel ?? t("common.delete")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeleteConfirmModal;
